import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    TextField,
    Typography,
} from '@mui/material';
import { JSX, useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { fieldProps, useValidatedForm } from '../../../library/formValidation';
import { IRoom, RoomFormValues, toRoomFormValues } from '../../../library/models/room';
import { CatalogTypeChoice } from '../../../library/models/serviceCatalog';
import { useApiFormError } from '../../../library/useApiFormError';
import { useIsCompact } from '../../../library/useIsCompact';
import { numericSlotProps, ROOM_CAPACITY_BOUNDS } from '../../../library/validationRules';
import ResourcesIds from '../../../resources/ResourcesIds';
import { createRoomSchema } from './roomSchema';
import {
    blockedHintStyles,
    choiceContextStyles,
    choicesBoxStyles,
    descriptionStyles,
    dialogActionsStyles,
    dialogFormStyles,
    dialogTitleStyles,
    fieldsBoxStyles,
    formAlertStyles,
} from './roomFormDialog.styles';

interface RoomFormDialogProps {
    isOpen: boolean;
    /** The room being edited, or null to add one. */
    room: IRoom | null;
    /** Every room of the clinic, for telling which services another one already holds. */
    rooms: IRoom[];
    /** The clinic's disciplines, one entry each, as the therapist services dialog lists them. */
    choices: CatalogTypeChoice[];
    onClose: () => void;
    /** Rejects with the API error, which this dialog maps back onto its fields. */
    onSubmit: (values: RoomFormValues) => Promise<unknown>;
}

/**
 * One kind of room: what it is called, how many the clinic has, and which services need one.
 *
 * A service is in one room at most -- that is what lets the API tell, for any booking, which
 * room it competes for -- so a service another room already holds is shown disabled with that
 * room's name rather than left for the API to refuse.
 */
export const RoomFormDialog = (props: RoomFormDialogProps): JSX.Element => {
    const { t: translate } = useTranslation();
    const isCompact = useIsCompact();

    const schema = useMemo(() => createRoomSchema(translate), [translate]);

    const {
        control, register, handleSubmit, reset, setError,
        formState: { errors, isValid, isSubmitting },
    } = useValidatedForm<RoomFormValues>(schema, { defaultValues: toRoomFormValues(null) });

    const knownFields: string[] = useMemo(() => Object.keys(schema.fields), [schema]);
    const { formError, report, clear } = useApiFormError<RoomFormValues>(
        setError, knownFields, translate, ResourcesIds.ROOMS_SAVE_ERROR);

    /** Which other room holds each service that is not free to tick here. */
    const takenBy: Map<number, string> = useMemo(() => new Map(
        props.rooms
            .filter(other => other.id !== props.room?.id)
            .flatMap(other => other.catalogTypeIds.map((typeId): [number, string] => [typeId, other.name]))
    ), [props.rooms, props.room]);

    // Reseeded on every open, so the dialog never shows the previous room's values.
    useEffect(() => {
        if (props.isOpen) {
            reset(toRoomFormValues(props.room));
            clear();
        }
    }, [props.isOpen, props.room, reset, clear]);

    const onValid = async (values: RoomFormValues): Promise<void> => {
        clear();

        try {
            await props.onSubmit(values);
        } catch (error: unknown) {
            report(error);
        }
    };

    return (
        <Dialog open={props.isOpen} onClose={props.onClose} maxWidth="sm" fullWidth fullScreen={isCompact}>
            <DialogTitle sx={dialogTitleStyles}>
                {translate(props.room ? ResourcesIds.ROOMS_EDIT_TITLE : ResourcesIds.ROOMS_CREATE_TITLE)}
            </DialogTitle>

            <Box component="form" onSubmit={handleSubmit(onValid)} noValidate sx={dialogFormStyles}>
                <DialogContent>
                    {formError && <Alert severity="error" sx={formAlertStyles}>{formError}</Alert>}

                    <Box sx={fieldsBoxStyles}>
                        <TextField
                            {...register('name')}
                            fullWidth
                            autoFocus
                            label={translate(ResourcesIds.ROOMS_NAME_LABEL)}
                            {...fieldProps(errors, 'name')}
                        />

                        <TextField
                            {...register('capacity')}
                            type="number"
                            fullWidth
                            label={translate(ResourcesIds.ROOMS_CAPACITY_LABEL)}
                            slotProps={numericSlotProps(ROOM_CAPACITY_BOUNDS)}
                            {...fieldProps(errors, 'capacity', translate(ResourcesIds.ROOMS_CAPACITY_HELPER))}
                        />
                    </Box>

                    <Typography variant="body2" sx={descriptionStyles}>
                        {translate(ResourcesIds.ROOMS_SERVICES_DESCRIPTION)}
                    </Typography>

                    {props.choices.length === 0 ? (
                        <Alert severity="info">
                            {translate(ResourcesIds.THERAPIST_SERVICES_NO_CATALOG)}
                        </Alert>
                    ) : (
                        // One Controller around the list, not one per tick as the therapist
                        // dialog has: FormControlLabel hands `disabled` to its control, and a
                        // disabled Controller drops the whole field from the submitted values.
                        <Controller
                            name="catalogTypeIds"
                            control={control}
                            render={({ field }) => (
                                <Box sx={choicesBoxStyles}>
                                    {props.choices.map(choice => {
                                        const otherRoom: string | undefined = takenBy.get(choice.id);

                                        return (
                                            <FormControlLabel
                                                key={choice.id}
                                                disabled={otherRoom !== undefined}
                                                control={
                                                    <Checkbox
                                                        checked={field.value.includes(choice.id)}
                                                        onChange={() => field.onChange(
                                                            field.value.includes(choice.id)
                                                                ? field.value.filter(id => id !== choice.id)
                                                                : [...field.value, choice.id]
                                                        )}
                                                    />
                                                }
                                                label={
                                                    <>
                                                        {choice.name}
                                                        <Typography component="span" variant="caption" sx={choiceContextStyles}>
                                                            {otherRoom === undefined
                                                                ? choice.context
                                                                : translate(ResourcesIds.ROOMS_SERVICE_TAKEN, { room: otherRoom })}
                                                        </Typography>
                                                    </>
                                                }
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                        />
                    )}
                </DialogContent>

                <DialogActions sx={dialogActionsStyles}>
                    {!isValid && (
                        <Typography variant="caption" color="text.secondary" sx={blockedHintStyles}>
                            {translate(ResourcesIds.FORM_BLOCKED_SAVE)}
                        </Typography>
                    )}
                    <Button onClick={props.onClose} variant="outlined" disabled={isSubmitting}>
                        {translate(ResourcesIds.CANCEL_TEXT)}
                    </Button>
                    <Button type="submit" variant="contained" disabled={!isValid || isSubmitting}>
                        {translate(ResourcesIds.SAVE_CHANGES)}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};
